import {
  DOCK_GAP,
  DOCK_MAX_PANEL_WIDTH,
  DOCK_MIN_PANEL_WIDTH,
  DOCK_PANEL_WIDTH,
  DOCK_SIDEBAR_COLUMN_WIDTH,
  autoTree,
  columnCount,
  completeTree,
  dividerWeights,
  insertPanel,
  minimumDockHeight,
  panelIds,
  readSplits,
  resizeTree,
  treeRects,
  visibleTree,
  withoutPanel
} from "./chunk-RBO2C7NT.js";
import {
  Button
} from "./chunk-QXEA2YZ6.js";
import {
  Icon
} from "./chunk-7IEFGXFA.js";
import {
  cn
} from "./chunk-SBEJSA5Q.js";

// src/WorkspaceDock.tsx
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { jsx, jsxs } from "react/jsx-runtime";
var DockContext = createContext(null);
var DockPanelContext = createContext(null);
function readLayout(key) {
  try {
    const value = JSON.parse(localStorage.getItem(key) ?? "{}");
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    return Object.fromEntries(Object.entries(value).filter((entry) => {
      const p = entry[1];
      return p && typeof p === "object" && (p.side === "left" || p.side === "right") && Number.isFinite(p.order);
    }));
  } catch {
    return {};
  }
}
function readWidths(key) {
  try {
    const value = JSON.parse(localStorage.getItem(key) ?? "{}");
    if (!value || typeof value !== "object") return {};
    const record = value;
    return Object.fromEntries(["left", "right"].flatMap((side) => typeof record[side] === "number" && Number.isFinite(record[side]) && record[side] > 0 ? [[side, record[side]]] : []));
  } catch {
    return {};
  }
}
var RESIZE_STEP = 16;
function ResizeHandle({ label, orientation, style, value, onResize, onReset }) {
  const start = useRef(null);
  const cursor = orientation === "vertical" ? "col-resize" : "row-resize";
  const coordinate = (event) => orientation === "vertical" ? event.clientX : event.clientY;
  const end = (event) => {
    if (start.current?.pointerId !== event.pointerId) return;
    start.current = null;
    document.body.style.removeProperty("cursor");
    document.body.style.removeProperty("user-select");
    event.currentTarget.removeAttribute("data-resizing");
  };
  const keydown = (event) => {
    const step = event.shiftKey ? RESIZE_STEP * 4 : RESIZE_STEP;
    const delta = orientation === "vertical" ? event.key === "ArrowLeft" ? -step : event.key === "ArrowRight" ? step : 0 : event.key === "ArrowUp" ? -step : event.key === "ArrowDown" ? step : 0;
    if (event.key === "Enter") {
      event.preventDefault();
      onReset();
      return;
    }
    if (!delta) return;
    event.preventDefault();
    event.stopPropagation();
    onResize(0, "start");
    onResize(delta, "move");
  };
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "separator",
      tabIndex: 0,
      "aria-label": label,
      "aria-orientation": orientation,
      "aria-valuenow": Math.round(value),
      title: `Drag to resize \xB7 double-click to reset`,
      "data-dock-resize-handle": orientation,
      className: "group absolute z-20 flex items-center justify-center pointer-events-auto touch-none outline-none",
      style: { ...style, cursor },
      onPointerDown: (event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        event.currentTarget.dataset.resizing = "true";
        start.current = { pointerId: event.pointerId, origin: coordinate(event) };
        document.body.style.cursor = cursor;
        document.body.style.userSelect = "none";
        onResize(0, "start");
      },
      onPointerMove: (event) => {
        if (start.current?.pointerId === event.pointerId) onResize(coordinate(event) - start.current.origin, "move");
      },
      onPointerUp: end,
      onPointerCancel: end,
      onLostPointerCapture: end,
      onDoubleClick: onReset,
      onKeyDown: keydown,
      children: /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: cn("rounded-full bg-primary opacity-0 transition-opacity group-hover:opacity-60 group-focus-visible:opacity-100 group-data-[resizing=true]:opacity-100", orientation === "vertical" ? "h-full w-0.5" : "h-0.5 w-full") })
    }
  );
}
function WorkspaceDock({ children, storageKey = "genie.workspace.panel-layout", className, onSidesChange, leftInset = 10, rightInset = 10, sidebarHost }) {
  const [layout, setLayout] = useState(() => readLayout(storageKey));
  const [splits, setSplits] = useState(() => readSplits(`${storageKey}.splits`));
  const [railWidths, setRailWidths] = useState(() => readWidths(`${storageKey}.widths`));
  const [left, setLeft] = useState(null);
  const [right, setRight] = useState(null);
  const [target, setTarget] = useState(null);
  const [maximized, setMaximized] = useState(null);
  const [panels, setPanels] = useState({});
  const [{ width, height }, setSize] = useState({ width: 0, height: 0 });
  const [container] = useState(() => document.createElement("div"));
  const anchor = useRef(null);
  const sidebar = Boolean(sidebarHost);
  const root = useRef(null);
  const rails = useMemo(() => ({ left, right }), [left, right]);
  const register = useCallback((id, state) => setPanels((items) => ({ ...items, [id]: state })), []);
  const unregister = useCallback((id) => setPanels((items) => {
    const next = { ...items };
    delete next[id];
    return next;
  }), []);
  useLayoutEffect(() => {
    container.style.height = sidebarHost ? "100%" : "";
    container.style.minHeight = "0";
    const parent = sidebarHost ?? anchor.current?.parentElement;
    if (parent && container.parentElement !== parent) parent.appendChild(container);
  }, [container, sidebarHost]);
  useEffect(() => () => container.remove(), [container]);
  useLayoutEffect(() => {
    if (!root.current) return;
    const observer = new ResizeObserver(([entry]) => setSize({ width: entry.contentRect.width, height: entry.contentRect.height }));
    observer.observe(root.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(layout));
    } catch {
    }
  }, [layout, storageKey]);
  useEffect(() => {
    try {
      localStorage.setItem(`${storageKey}.splits`, JSON.stringify(splits));
    } catch {
    }
  }, [splits, storageKey]);
  useEffect(() => {
    try {
      localStorage.setItem(`${storageKey}.widths`, JSON.stringify(railWidths));
    } catch {
    }
  }, [railWidths, storageKey]);
  const groups = { left: [], right: [] };
  const panelMap = {};
  for (const [id, panel] of Object.entries(panels)) {
    panelMap[id] = { id, ...panel, columns: sidebar ? 1 : panel.columns, tall: panel.tall || panel.columns === 2 };
    if (panel.visible) groups[sidebar ? "right" : layout[id]?.side ?? "right"].push(panelMap[id]);
  }
  for (const side of ["left", "right"]) groups[side].sort((a, b) => (layout[a.id]?.order ?? 100) - (layout[b.id]?.order ?? 100));
  const keyFor = (side) => sidebar ? "sidebar" : side;
  const configured = { left: sidebar ? void 0 : splits.left, right: splits[keyFor("right")] };
  const trees = { left: void 0, right: void 0 };
  const visibleTrees = { left: void 0, right: void 0 };
  for (const side of ["left", "right"]) {
    const expanded = groups[side].filter((panel) => !panel.collapsed);
    const budget = sidebar ? Math.max(1, Math.floor((width + DOCK_GAP) / (DOCK_SIDEBAR_COLUMN_WIDTH + DOCK_GAP))) : (expanded.length >= 3 ? 2 : 1) + expanded.filter((panel) => panel.tall).reduce((sum, panel) => sum + (panel.columns ?? 1), 0);
    trees[side] = configured[side] ? completeTree(configured[side], groups[side]) : autoTree(groups[side].map((panel) => panelMap[panel.id]), budget);
    visibleTrees[side] = visibleTree(trees[side], new Set(groups[side].map((panel) => panel.id)));
    if (sidebar && maximized && groups[side].some((panel) => panel.id === maximized)) visibleTrees[side] = { panel: maximized };
  }
  const wanted = { left: columnCount(visibleTrees.left, panelMap), right: columnCount(visibleTrees.right, panelMap) };
  const available = sidebar ? width : Math.max(0, width - leftInset - rightInset - 16);
  const gaps = (count) => Math.max(0, count - 1) * DOCK_GAP;
  const columnsFit = available >= (wanted.left + wanted.right) * DOCK_MIN_PANEL_WIDTH + gaps(wanted.left) + gaps(wanted.right);
  const columns = { left: columnsFit ? wanted.left : Math.min(1, wanted.left), right: columnsFit ? wanted.right : Math.min(1, wanted.right) };
  const sharedWidth = Math.max(0, (available - gaps(columns.left) - gaps(columns.right)) / (columns.left + columns.right || 1));
  const panelWidth = Math.min(sidebar ? sharedWidth : DOCK_PANEL_WIDTH, sharedWidth, DOCK_MAX_PANEL_WIDTH);
  const sizes = { left: columns.left * panelWidth + gaps(columns.left), right: columns.right * panelWidth + gaps(columns.right) };
  const columnLimit = { left: DOCK_MIN_PANEL_WIDTH, right: DOCK_MIN_PANEL_WIDTH };
  if (!sidebar) for (const side of ["right", "left"]) {
    if (!columns[side]) continue;
    const other = side === "left" ? "right" : "left";
    columnLimit[side] = Math.min(DOCK_MAX_PANEL_WIDTH, Math.max(DOCK_MIN_PANEL_WIDTH, (available - sizes[other] - DOCK_GAP * 2 - gaps(columns[side])) / columns[side]));
    const saved = railWidths[side];
    if (saved) sizes[side] = columns[side] * Math.min(Math.max(saved, DOCK_MIN_PANEL_WIDTH), columnLimit[side]) + gaps(columns[side]);
  }
  const overflow = { left: false, right: false };
  const rects = {};
  const dividers = { left: [], right: [] };
  for (const side of ["left", "right"]) {
    const needed = minimumDockHeight(visibleTrees[side], sizes[side], panelMap);
    overflow[side] = needed > height;
    treeRects(visibleTrees[side], { x: 0, y: 0, width: sizes[side], height: Math.max(height, needed) }, panelMap, rects, { fill: sidebar, dividers: dividers[side] });
  }
  const railLeft = (side) => sidebar ? side === "left" ? 0 : Math.max(0, width - sizes.right) / 2 : side === "left" ? leftInset : width - rightInset - sizes.right;
  const resizing = useRef(null);
  const resizeSplit = (side, divider, delta, phase) => {
    if (phase === "start") {
      resizing.current = { tree: trees[side], divider };
      return;
    }
    const snapshot = resizing.current;
    if (!snapshot?.divider) return;
    const weights = dividerWeights(snapshot.divider, delta);
    const visible = new Set(groups[side].map((panel) => panel.id));
    const tree = resizeTree(snapshot.tree, snapshot.divider.axis, visible, snapshot.divider.groups, weights);
    if (tree) setSplits((previous) => ({ ...previous, [keyFor(side)]: tree }));
  };
  const resetSplit = (side, divider) => {
    const tree = resizeTree(trees[side], divider.axis, new Set(groups[side].map((panel) => panel.id)), divider.groups, divider.groups.map(() => 1));
    if (tree) setSplits((previous) => ({ ...previous, [keyFor(side)]: tree }));
  };
  const resizeRail = (side, delta, phase) => {
    const count = columns[side] || 1;
    if (phase === "start") {
      resizing.current = { tree: void 0, column: (sizes[side] - gaps(count)) / count };
      return;
    }
    const column = resizing.current?.column;
    if (column === void 0) return;
    const grown = side === "right" ? -delta : delta;
    setRailWidths((previous) => ({ ...previous, [side]: Math.round(Math.min(Math.max(column + grown / count, DOCK_MIN_PANEL_WIDTH), columnLimit[side])) }));
  };
  const drop = (id, target2) => {
    if (target2.panelId === id || !panels[id]?.visible) return;
    const side = sidebar ? "right" : target2.side;
    const sourceSide = sidebar ? "right" : layout[id]?.side ?? "right";
    const sourceKey = keyFor(sourceSide), targetKey = keyFor(side);
    setSplits((previous) => {
      const next = { ...previous };
      if (sourceKey !== targetKey) {
        const source = withoutPanel(trees[sourceSide], id);
        if (source) next[sourceKey] = source;
        else delete next[sourceKey];
      }
      const destination = withoutPanel(trees[side], id);
      if (target2.panelId && !panelIds(destination).includes(target2.panelId)) return previous;
      next[targetKey] = insertPanel(destination, id, target2.panelId, target2.edge);
      return next;
    });
    if (!sidebar) setLayout((previous) => ({ ...previous, [id]: { side, order: Math.max(0, ...Object.values(previous).map((p) => p.order)) + 1 } }));
  };
  const locate = (id, x, y) => {
    const bounds = root.current?.getBoundingClientRect();
    if (!bounds || x < bounds.left || x > bounds.right || y < bounds.top || y > bounds.bottom) return null;
    for (const side2 of ["left", "right"]) for (const element of Array.from(rails[side2]?.children ?? [])) {
      if (!(element instanceof HTMLElement) || element.dataset.panelId === id || element.style.display === "none") continue;
      const box = element.getBoundingClientRect();
      if (x < box.left || x > box.right || y < box.top || y > box.bottom || !box.width || !box.height) continue;
      const distances = [["left", (x - box.left) / box.width], ["right", (box.right - x) / box.width], ["top", (y - box.top) / box.height], ["bottom", (box.bottom - y) / box.height]];
      const edge = distances.sort((a, b) => a[1] - b[1])[0][0];
      const horizontal = edge === "left" || edge === "right";
      return { side: side2, panelId: element.dataset.panelId, edge, box: { x: box.left - bounds.left + (edge === "right" ? box.width / 2 : 0), y: box.top - bounds.top + (edge === "bottom" ? box.height / 2 : 0), width: horizontal ? box.width / 2 : box.width, height: horizontal ? box.height : box.height / 2 } };
    }
    if (sidebar) return null;
    const side = x < bounds.left + bounds.width / 2 ? "left" : "right";
    if (side === (layout[id]?.side ?? "right")) return null;
    const railWidth = sizes[side] || panelWidth || DOCK_PANEL_WIDTH;
    return { side, edge: "bottom", box: { x: side === "left" ? leftInset : bounds.width - railWidth - rightInset, y: 0, width: railWidth, height: bounds.height } };
  };
  const move = (id, edge, split = false) => {
    if (!sidebar && !split && (edge === "left" || edge === "right")) {
      drop(id, { side: edge, edge: "bottom", box: { x: 0, y: 0, width: 0, height: 0 } });
      return;
    }
    const side = sidebar ? "right" : layout[id]?.side ?? "right";
    const ids = panelIds(visibleTree(trees[side], new Set(groups[side].map((p) => p.id))));
    const index = ids.indexOf(id);
    if (index < 0 || ids.length < 2) return;
    const before = edge === "left" || edge === "top";
    const neighbor = ids[index + (before ? -1 : 1)] ?? ids[index + (before ? 1 : -1)];
    drop(id, { side, panelId: neighbor, edge, box: { x: 0, y: 0, width: 0, height: 0 } });
  };
  const maximizedVisible = Boolean(maximized && sidebar && panels[maximized]?.visible);
  useEffect(() => {
    if (maximized && !maximizedVisible) setMaximized(null);
  }, [maximized, maximizedVisible]);
  const toggleMaximize = useCallback((id) => setMaximized((current) => current === id ? null : id), []);
  const leftOpen = groups.left.length > 0, rightOpen = groups.right.length > 0;
  useEffect(() => onSidesChange?.({ left: leftOpen, right: rightOpen, leftWidth: sizes.left, rightWidth: sizes.right }), [leftOpen, rightOpen, sizes.left, sizes.right, onSidesChange]);
  const value = { rails, layout, sidebar, rects, move, locate, drop, preview: setTarget, register, unregister, maximized: maximizedVisible ? maximized : null, toggleMaximize };
  const previewLabel = target?.panelId ? { left: "Place beside \xB7 left", right: "Place beside \xB7 right", top: "Stack above", bottom: "Stack below" }[target.edge] : target ? `Dock ${target.side}` : "";
  return /* @__PURE__ */ jsxs(DockContext.Provider, { value, children: [
    /* @__PURE__ */ jsx("span", { ref: anchor, hidden: true }),
    createPortal(/* @__PURE__ */ jsxs("div", { ref: root, className: cn(sidebar ? "relative h-full min-h-0 w-full pointer-events-none z-10" : "absolute inset-x-0 top-2.5 bottom-2.5 pointer-events-none z-10", className), children: [
      ["left", "right"].map((side) => {
        const isTiled = columns[side] > 1;
        return /* @__PURE__ */ jsx(
          "div",
          {
            ref: side === "left" ? setLeft : setRight,
            "data-workspace-dock": side,
            "data-dock-tiled": isTiled,
            "data-dock-custom": "true",
            className: cn("genie-dock-rail absolute top-0 bottom-0 flex flex-col gap-2.5 bg-transparent", overflow[side] ? "overflow-y-auto overflow-x-hidden pointer-events-auto" : "pointer-events-none"),
            "data-dock-overflow": overflow[side],
            style: { [side]: sidebar ? 0 : side === "left" ? leftInset : rightInset, width: sizes[side] }
          },
          side
        );
      }),
      ["left", "right"].map((side) => (overflow[side] ? [] : dividers[side]).map((divider) => {
        const vertical = divider.axis === "horizontal";
        const names = divider.groups.filter((_, index) => divider.between.includes(index)).map((ids) => ids.map((panelId) => panels[panelId]?.title ?? panelId).join(" and "));
        return /* @__PURE__ */ jsx(
          ResizeHandle,
          {
            label: `Resize ${names.join(" / ")}`,
            orientation: vertical ? "vertical" : "horizontal",
            value: divider.pixels[divider.between[0]] ?? 0,
            style: { left: railLeft(side) + divider.rect.x, top: divider.rect.y, width: divider.rect.width, height: divider.rect.height },
            onResize: (delta, phase) => resizeSplit(side, divider, delta, phase),
            onReset: () => resetSplit(side, divider)
          },
          `${side}:${divider.axis}:${divider.groups[divider.between[0]].join()}|${divider.groups[divider.between[1]].join()}`
        );
      })),
      !sidebar && ["left", "right"].map((side) => {
        const bottom = Math.max(0, ...groups[side].map((panel) => rects[panel.id] ? rects[panel.id].y + rects[panel.id].height : 0));
        if (!columns[side] || !bottom || overflow[side]) return null;
        return /* @__PURE__ */ jsx(
          ResizeHandle,
          {
            label: `Resize ${side} panels`,
            orientation: "vertical",
            value: sizes[side],
            style: { left: side === "left" ? railLeft(side) + sizes[side] : railLeft(side) - DOCK_GAP, top: 0, width: DOCK_GAP, height: bottom },
            onResize: (delta, phase) => resizeRail(side, delta, phase),
            onReset: () => setRailWidths((previous) => {
              const next = { ...previous };
              delete next[side];
              return next;
            })
          },
          `rail:${side}`
        );
      }),
      target && /* @__PURE__ */ jsx(
        "div",
        {
          "data-dock-preview": target.side,
          "data-drop-edge": target.edge,
          className: "absolute rounded-lg border-2 border-primary bg-primary/10 pointer-events-none z-[70]",
          style: { left: target.box.x, top: target.box.y, width: target.box.width, height: target.box.height },
          children: /* @__PURE__ */ jsx("span", { className: "absolute top-2 left-2 rounded bg-popover px-2 py-1 text-xs text-foreground", children: previewLabel })
        }
      ),
      children,
      /* @__PURE__ */ jsx("span", { role: "status", "aria-live": "polite", className: "sr-only", children: target ? `Release to ${previewLabel.toLowerCase()}` : "" })
    ] }), container)
  ] });
}
function WorkspaceDockPanel({ id, title, children, visible = true, columns = 1, tall = false, className, contentHost }) {
  const dock = useContext(DockContext);
  if (!dock) throw new Error("WorkspaceDockPanel requires WorkspaceDock");
  const [container] = useState(() => document.createElement("div"));
  const [collapsed, setCollapsed] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(34);
  const savedPlacement = dock.layout[id] ?? { side: "right", order: 100 };
  const placement = { ...savedPlacement, side: dock.sidebar ? "right" : savedPlacement.side };
  const rect = contentHost ? void 0 : dock.rects[id];
  const coveredByMaximized = !contentHost && Boolean(dock.maximized && dock.maximized !== id);
  const isMaximized = !contentHost && dock.maximized === id;
  const drag = useRef(null);
  const current = useRef({ dock, placement });
  current.current = { dock, placement };
  useEffect(() => dock.register(id, { title, visible: visible && !contentHost, collapsed, headerHeight, columns, tall }), [dock.register, id, title, visible, contentHost, collapsed, headerHeight, columns, tall]);
  useEffect(() => () => dock.unregister(id), [dock.unregister, id]);
  useLayoutEffect(() => {
    container.className = cn("min-h-0 flex flex-col pointer-events-auto", className);
    container.dataset.panelId = id;
    container.dataset.panelSide = placement.side;
    container.dataset.collapsed = String(collapsed);
    container.style.display = visible && !coveredByMaximized ? "flex" : "none";
    container.dataset.maximized = String(isMaximized);
    container.style.order = String(placement.order + (collapsed ? 1e3 : 0));
    container.style.maxWidth = "100%";
    container.style.position = rect ? "absolute" : "";
    container.style.left = rect ? `${rect.x}px` : "";
    container.style.top = rect ? `${rect.y}px` : "";
    container.style.width = rect ? `${rect.width}px` : "";
    container.style.height = rect ? `${rect.height}px` : "";
  }, [container, id, visible, coveredByMaximized, isMaximized, className, placement.side, placement.order, collapsed, rect?.x, rect?.y, rect?.width, rect?.height]);
  useLayoutEffect(() => {
    const rail = contentHost ?? dock.rails[placement.side];
    if (!rail || container.parentElement === rail) return;
    const focused = container.contains(document.activeElement) ? document.activeElement : null;
    rail.appendChild(container);
    focused?.focus({ preventScroll: true });
  }, [container, dock.rails, placement.side, contentHost]);
  useEffect(() => () => container.remove(), [container]);
  useLayoutEffect(() => {
    let header = null;
    const resize = new ResizeObserver(() => update());
    const update = () => {
      const frame = container.querySelector("[data-panel-collapsed]");
      const next = frame?.getAttribute("data-panel-collapsed") === "true";
      container.style.flex = next ? "0 0 auto" : "";
      setCollapsed(next);
      const handle = frame?.querySelector("[data-panel-drag-handle]") ?? null;
      if (header !== handle) {
        if (header) resize.unobserve(header);
        header = handle;
        if (header) resize.observe(header);
      }
      if (frame && header) {
        const style = getComputedStyle(frame);
        const measured = header.getBoundingClientRect().height;
        if (measured > 0) setHeaderHeight(Math.ceil(measured + parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth)));
      }
    };
    const observer = new MutationObserver(update);
    observer.observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-panel-collapsed"] });
    update();
    return () => {
      observer.disconnect();
      resize.disconnect();
    };
  }, [container]);
  useEffect(() => {
    if (contentHost) return;
    const isHandle = (target) => target instanceof Element && target.closest("[data-panel-drag-handle]") && !target.closest('button,input,textarea,select,a,[contenteditable="true"]');
    const clear = () => {
      drag.current = null;
      container.style.transform = "";
      container.style.zIndex = "";
      container.style.opacity = "";
      container.removeAttribute("data-dragging");
      current.current.dock.preview(null);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
      window.removeEventListener("keydown", escape);
      window.removeEventListener("blur", cancel);
    };
    const move = (event) => {
      const d = drag.current;
      if (!d || event.pointerId !== d.pointerId) return;
      const dx = event.clientX - d.x, dy = event.clientY - d.y;
      if (!d.active && Math.hypot(dx, dy) < 5) return;
      d.active = true;
      d.target = current.current.dock.locate(id, event.clientX, event.clientY);
      container.style.transform = `translate(${dx}px, ${dy}px)`;
      container.style.zIndex = "60";
      container.style.opacity = "0.85";
      container.dataset.dragging = "true";
      current.current.dock.preview(d.target);
      event.preventDefault();
    };
    const up = (event) => {
      const d = drag.current;
      if (!d || event.pointerId !== d.pointerId) return;
      if (d.active && d.target) current.current.dock.drop(id, d.target);
      clear();
    };
    const cancel = () => clear();
    const escape = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        clear();
      }
    };
    const down = (event) => {
      if (event.button !== 0 || !isHandle(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
      drag.current = { x: event.clientX, y: event.clientY, pointerId: event.pointerId, active: false, target: null };
      window.addEventListener("pointermove", move, { passive: false });
      window.addEventListener("pointerup", up);
      window.addEventListener("pointercancel", cancel);
      window.addEventListener("keydown", escape);
      window.addEventListener("blur", cancel);
    };
    const keydown = (event) => {
      if (!isHandle(event.target) || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
      event.preventDefault();
      event.stopPropagation();
      const edge = { ArrowLeft: "left", ArrowRight: "right", ArrowUp: "top", ArrowDown: "bottom" }[event.key];
      current.current.dock.move(id, edge, event.shiftKey);
    };
    const doubleClick = (event) => {
      if (current.current.dock.sidebar && isHandle(event.target)) current.current.dock.toggleMaximize(id);
    };
    container.addEventListener("pointerdown", down);
    container.addEventListener("keydown", keydown);
    container.addEventListener("dblclick", doubleClick);
    return () => {
      clear();
      container.removeEventListener("pointerdown", down);
      container.removeEventListener("keydown", keydown);
      container.removeEventListener("dblclick", doubleClick);
    };
  }, [container, id, contentHost]);
  const panelControls = useMemo(() => dock.sidebar && !contentHost ? { title, maximized: isMaximized, toggle: () => dock.toggleMaximize(id) } : null, [dock.sidebar, dock.toggleMaximize, contentHost, title, isMaximized, id]);
  return createPortal(/* @__PURE__ */ jsx(DockPanelContext.Provider, { value: panelControls, children: /* @__PURE__ */ jsx("div", { "aria-label": `${title} dock`, className: "contents", children }) }), container);
}
function PanelMaximizeButton() {
  const panel = useContext(DockPanelContext);
  if (!panel) return null;
  const label = `${panel.maximized ? "Restore" : "Maximize"} ${panel.title}`;
  return /* @__PURE__ */ jsx(
    Button,
    {
      type: "button",
      variant: "ghost",
      size: "icon-xs",
      "data-panel-maximize": true,
      "aria-label": label,
      "aria-pressed": panel.maximized,
      title: label,
      onClick: panel.toggle,
      className: "h-6 w-6 shrink-0 rounded-[6px] p-1 text-muted-foreground hover:text-foreground",
      children: /* @__PURE__ */ jsx(Icon, { name: panel.maximized ? "minimize-2" : "maximize-2", className: "h-4 w-4" })
    }
  );
}
var panelDragHandle = (title) => ({
  "data-panel-drag-handle": true,
  tabIndex: 0,
  role: "group",
  "aria-label": `Move ${title} panel`,
  title: "Drag to a panel edge to split. Up/Down stacks; Shift+Left/Right places beside. Left/Right moves between sides.",
  style: { cursor: "grab", touchAction: "none", userSelect: "none" }
});

export {
  WorkspaceDock,
  WorkspaceDockPanel,
  PanelMaximizeButton,
  panelDragHandle
};
//# sourceMappingURL=chunk-YX32U7BA.js.map